import { Helmet } from 'react-helmet-async';
import { Outlet, matchPath, useLocation } from 'react-router-dom';

import { PrivacyToggleProvider } from '@eluve/blocks';
import {
  Icon,
  Layout,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuNavButton,
} from '@eluve/components';
import { MedplumProvider } from '@eluve/medplum-client';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useIsPromptTemplateCreator,
  useIsTemplatePublisher,
  useIsTenantAdminFromSession,
} from '@eluve/session-helpers';
import { ChatAccessGate, FeatureFlaggedComponent } from '@eluve/smart-blocks';

import { appConfig } from '../../../../../config';
import { TenantUserSettings } from '../../../TenantUserSettings';
import { useIsCarePlanFeatureEnabled } from '../../CarePlanFeatureGate';
import { ImpersonationExpiryMonitor } from '../ImpersonationExpiryMonitor';
import { TranscriptionWidget } from '../appointments/machines/transcription/components/TranscriptionWidget';

const SettingsNavItem = ({ label, path }: { label: string; path: string }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const { pathname } = useLocation();

  const to = `/organizations/${tenantId}/settings/${path}`;

  // Wildcard match keeps the tab active while a drill-in sub-route is open
  const isActive =
    matchPath(to, pathname) != null || matchPath(`${to}/*`, pathname) != null;

  return (
    <SidebarMenuItem>
      <SidebarMenuNavButton to={to} isActive={isActive}>
        <span>{label}</span>
      </SidebarMenuNavButton>
    </SidebarMenuItem>
  );
};

/**
 * Full-screen takeover shell for the consolidated settings experience:
 * settings-only sidebar (grouped, gated nav) + per-tab pages via Outlet.
 */
export const SettingsShell = () => {
  const tenantId = useAssignedTenantIdFromParams();

  const isTenantAdmin = useIsTenantAdminFromSession();
  const hasInsights = useIsPostHogFeatureFlagEnabled(
    'EXTENSION_PREVISIT_INSIGHTS',
  );
  const isPromptTemplateCreator = useIsPromptTemplateCreator();
  const isTemplatePublisher = useIsTemplatePublisher();
  const hasTemplates = isPromptTemplateCreator || isTemplatePublisher;
  const hasCarePlans = useIsCarePlanFeatureEnabled();

  return (
    <>
      <Helmet>
        <title>Settings | Eluve</title>
      </Helmet>
      <Layout>
        <Sidebar variant="inset" collapsible="offcanvas">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuNavButton
                  to={`/organizations/${tenantId}/home`}
                  className="justify-center pr-6"
                >
                  <Icon name="ChevronLeft" size="auto" />
                  <span>Back to app</span>
                </SidebarMenuNavButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Account</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SettingsNavItem label="User Settings" path="user" />
                  <SettingsNavItem label="Audio & Display" path="audio" />
                  <SettingsNavItem label="Shortcuts" path="shortcuts" />
                  <SettingsNavItem label="App Grants" path="app-grants" />
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Preferences</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SettingsNavItem label="Note" path="note" />
                  {hasTemplates && (
                    <SettingsNavItem label="Templates" path="templates" />
                  )}
                  <ChatAccessGate>
                    <SettingsNavItem label="Chat" path="chat" />
                  </ChatAccessGate>
                  {hasInsights && (
                    <SettingsNavItem label="Insights" path="insights" />
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            {isTenantAdmin && (
              <SidebarGroup>
                <SidebarGroupLabel>Organization</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SettingsNavItem label="General" path="organization" />
                    <SettingsNavItem label="Members" path="members" />
                    <FeatureFlaggedComponent flag="SESSION_TYPES">
                      <SettingsNavItem
                        label="Session Types"
                        path="session-types"
                      />
                    </FeatureFlaggedComponent>
                    <FeatureFlaggedComponent flag="LOCATIONS">
                      <SettingsNavItem label="Locations" path="locations" />
                    </FeatureFlaggedComponent>
                    <FeatureFlaggedComponent flag="USER_GROUPS">
                      <SettingsNavItem label="User Groups" path="user-groups" />
                    </FeatureFlaggedComponent>
                    <FeatureFlaggedComponent flag="ENABLE_TENANT_ADMIN_REGENERATE_NOTE">
                      <SettingsNavItem
                        label="Note Recovery"
                        path="regenerate-notes"
                      />
                    </FeatureFlaggedComponent>
                    {hasCarePlans && (
                      <FeatureFlaggedComponent flag="MEDPLUM">
                        <SettingsNavItem label="Care Plans" path="care-plans" />
                      </FeatureFlaggedComponent>
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </SidebarContent>
        </Sidebar>
        <PrivacyToggleProvider>
          <TenantUserSettings>
            <ImpersonationExpiryMonitor />
            <MedplumProvider baseUrl={appConfig.VITE_MEDPLUM_BASE_URL}>
              <Outlet />
            </MedplumProvider>
          </TenantUserSettings>
        </PrivacyToggleProvider>
        <TranscriptionWidget />
      </Layout>
    </>
  );
};
